import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../../components/Layout";
import Breadcrumbs from "../../components/Breadcrumbs";
import toast, { Toaster } from "react-hot-toast";
import { get, post } from "../../services/ApiService";

const statuses = ["Open", "Closed"];

const ComplaintDetail = () => {
  const { id } = useParams();
  const [complaint, setComplaint] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [replyAuthor, setReplyAuthor] = useState("");
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    fetchComplaint();
  }, [id]);

  const fetchComplaint = async () => {
    try {
      setIsLoading(true);
      const res = await get(`api/complaints/${id}`);
      if (res.success) {
        setComplaint(res.complaint);
      } else {
        setComplaint(null);
      }
    } catch (error) {
      console.error(error.message);
      setComplaint(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddReply = async () => {
    if (!replyText.trim()) { toast.error("Please enter a reply"); return; }
    if (!replyAuthor.trim()) { toast.error("Please enter your name"); return; }

    try {
      setIsSending(true);
      const res = await post(`api/complaints/reply/${id}`, {
        text: replyText,
        author: replyAuthor,
      });
      if (res.success) {
        setComplaint(res.complaint);
        setReplyText("");
        setReplyAuthor("");
        toast.success("Reply added successfully");
      } else {
        toast.error(res.error || "Failed to add reply");
      }
    } catch (error) {
      toast.error("Failed to add reply");
    } finally {
      setIsSending(false);
    }
  };

  const changeStatus = async (newStatus) => {
    try {
      const res = await post(`api/complaints/status/${id}`, { status: newStatus });
      if (res.success) {
        setComplaint(res.complaint);
        toast.success(`Status changed to ${newStatus}`);
      } else {
        toast.error(res.error || "Failed to change status");
      }
    } catch (error) {
      toast.error("Failed to change status");
    }
  };

  const getStatusBadge = (status) => {
    const map = { Open: "bg-success", Closed: "bg-danger" };
    return map[status] || "bg-secondary";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="text-center mt-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      </Layout>
    );
  }

  if (!complaint) {
    return (
      <Layout>
        <div className="text-center mt-5">
          <h4>Complaint not found.</h4>
          <Link to="/complaints" className="btn btn-primary btn-sm mt-3">Back to Complaints</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Toaster position="top-center" reverseOrder={false} />

      {/* Header */}
      <div className="row mb-9 align-items-center justify-content-between">
        <div className="col-sm-6 mb-8 mb-sm-0">
          <h2 className="fs-4 mb-0">Complaint Detail</h2>
          <Breadcrumbs />
        </div>
        <div className="col-sm-6 col-md-4 d-flex flex-wrap justify-content-sm-end gap-2 align-items-center">
          <select
            className="form-select form-select-sm"
            style={{ width: "140px", outline: "none", boxShadow: "none" }}
            value={complaint.status}
            onChange={(e) => changeStatus(e.target.value)}
          >
            {statuses.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <Link to="/complaints" className="btn btn-sm btn-secondary rounded-pill px-4">
            <i className="fas fa-arrow-left me-1" />
            Back
          </Link>
        </div>
      </div>

      <div className="row">
        {/* Complaint Info */}
        <div className="col-lg-5 col-12 mb-4">
          <div className="card rounded-4 p-6 border-0 shadow-sm">
            <div className="d-flex justify-content-between align-items-start mb-4">
              <h5 className="mb-0 fw-bold">{complaint.complaintId}</h5>
              <span className={`badge ${getStatusBadge(complaint.status)}`} style={{ fontSize: "12px" }}>
                {complaint.status}
              </span>
            </div>

            <h6 className="fw-semibold mb-3">{complaint.title}</h6>

            <div className="mb-4">
              <p className="text-muted mb-0" style={{ lineHeight: "1.7" }}>{complaint.description}</p>
            </div>

            <hr />

            <div className="row">
              <div className="col-6 mb-3">
                <small className="text-muted d-block">Date</small>
                <span className="fw-semibold">{formatDate(complaint.date)}</span>
              </div>
              <div className="col-6 mb-3">
                <small className="text-muted d-block">Status</small>
                <span className={`badge ${getStatusBadge(complaint.status)}`}>{complaint.status}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Replies Section */}
        <div className="col-lg-7 col-12 mb-4">
          <div className="card rounded-4 p-6 border-0 shadow-sm">
            <h5 className="fw-bold mb-4">
              <i className="fas fa-comments me-2 text-primary" />
              Replies ({complaint.replies.length})
            </h5>

            {/* Reply List */}
            <div style={{ maxHeight: "400px", overflowY: "auto" }} className="mb-4">
              {complaint.replies.length > 0 ? (
                complaint.replies.map((reply, index) => (
                  <div key={reply._id || index} className="mb-3 p-4 rounded-3" style={{ backgroundColor: reply.author === "Admin" ? "#e8f4ed" : "#f8f9fa" }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold" style={{ fontSize: "14px" }}>
                        <i className={`fas ${reply.author === "Admin" ? "fa-user-shield text-primary" : "fa-user text-secondary"} me-2`} />
                        {reply.author}
                      </span>
                      <small className="text-muted">{formatDateTime(reply.date)}</small>
                    </div>
                    <p className="mb-0" style={{ fontSize: "14px", lineHeight: "1.6" }}>{reply.text}</p>
                  </div>
                ))
              ) : (
                <div className="text-center py-6">
                  <i className="fas fa-comment-slash text-muted" style={{ fontSize: "40px" }} />
                  <p className="text-muted mt-3">No replies yet</p>
                </div>
              )}
            </div>

            {/* Add Reply */}
            {complaint.status !== "Closed" ? (
              <div className="border-top pt-4">
                <h6 className="fw-semibold mb-3">Add Reply</h6>
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Your name"
                    value={replyAuthor}
                    onChange={(e) => setReplyAuthor(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <textarea
                    className="form-control"
                    placeholder="Write your reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={3}
                  />
                </div>
                <div className="d-flex justify-content-end">
                  <button className="btn btn-primary btn-sm rounded-pill px-4" onClick={handleAddReply} disabled={isSending}>
                    <i className="fas fa-paper-plane me-2" />
                    {isSending ? "Sending..." : "Send Reply"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="border-top pt-4 text-center">
                <span className="text-muted">
                  <i className="fas fa-lock me-1" />
                  This complaint is closed. No more replies allowed.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ComplaintDetail;
